import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import _ from 'lodash';

import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';

// Studio's URL says which entity it previews as, so a reload (or Edit in
// Studio from an entity page) keeps it. Never saved with the content.
export const ENTITY_APP_PARAM = 'entityApp';
export const ENTITY_PARAM = 'entity';

export interface EntityPreviewApp {
  app: EntityDetails.App;
  // The dashboard or report being edited is one of its tabs
  isTab: boolean;
}

export interface EntityPreview {
  // The entity apps Studio can preview as: those the content is a tab of
  // first
  apps: EntityPreviewApp[];
  app?: EntityDetails.App;
  entity?: EntityDetails.Entity;
  // The previewed entity's value of its primary field; absent until chosen
  value?: string;
  setApp: (appId: string | undefined) => void;
  setValue: (value: string) => void;
  // An entity app and one of its entities at once, as the picker chooses
  choose: (next: { appId: string; value: string }) => void;
}

// Which entity Studio previews its blocks as, as they'd draw on that
// entity's page. Content that's a tab of an entity app starts out previewing
// it (Edit from an entity page names the entity too).
const useEntityPreview = ({
  contentType,
  contentId,
}: {
  contentType: Studio.ContentType;
  // Absent for a new draft
  contentId?: string;
}): EntityPreview => {
  const { entityDefinitions, entityDetailsApps } = useContext(
    EntityDefinitionsContext,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const appId = searchParams.get(ENTITY_APP_PARAM) || undefined;
  const value = searchParams.get(ENTITY_PARAM) || undefined;

  const apps = useMemo((): EntityPreviewApp[] => {
    const isTab = (app: EntityDetails.App) =>
      !!contentId &&
      app.tabs.some((t) => t.type === contentType && t.typeId === contentId);
    return _.sortBy(
      entityDetailsApps
        .filter(
          (app) =>
            !app.isHidden &&
            entityDefinitions.some((e) => e.id === app.entityId),
        )
        .map((app) => ({ app, isTab: isTab(app) })),
      [(a) => (a.isTab ? 0 : 1), (a) => a.app.title.toLowerCase()],
    );
  }, [contentId, contentType, entityDefinitions, entityDetailsApps]);

  const app = appId ? apps.find((a) => a.app.id === appId)?.app : undefined;
  const entity = app
    ? entityDefinitions.find((e) => e.id === app.entityId)
    : undefined;

  const setParams = useCallback(
    (next: { appId?: string; value?: string }) =>
      setSearchParams(
        (params) => {
          [
            [ENTITY_APP_PARAM, next.appId],
            [ENTITY_PARAM, next.value],
          ].forEach(([key, v]) => {
            if (v) {
              params.set(key as string, v);
            } else {
              params.delete(key as string);
            }
          });
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const setApp = useCallback(
    (nextAppId: string | undefined) => setParams({ appId: nextAppId }),
    [setParams],
  );
  const setValue = useCallback(
    (nextValue: string) => setParams({ appId, value: nextValue }),
    [appId, setParams],
  );
  const choose = useCallback(
    (next: { appId: string; value: string }) => setParams(next),
    [setParams],
  );

  // Content that's an entity app's tab starts out previewing as that app's
  // entity, once (choosing None afterwards sticks)
  const hasDefaulted = useRef(false);
  useEffect(() => {
    if (hasDefaulted.current || apps.length === 0) {
      return;
    }
    hasDefaulted.current = true;
    const [first] = apps;
    if (!appId && first.isTab) {
      setApp(first.app.id);
    }
  }, [appId, apps, setApp]);

  return { apps, app, entity, value, setApp, setValue, choose };
};

export default useEntityPreview;
